import { useState, useEffect, useCallback } from 'react'
import { Share2, Check } from 'lucide-react'
import { Header, SettingsModal, StatsModal, OfflineBanner, LocationSearch } from './components'
import { SpeedTierToggle } from './components/FuelTypeToggle'
import { HybridPage } from './pages'
import { useSettings } from './hooks'
import { readUrlState } from './lib/urlState'

/** Background refresh interval in milliseconds (5 minutes) */
const BACKGROUND_REFRESH_INTERVAL = 5 * 60 * 1000

/**
 * Main app component with header navigation
 * Hybrid view shows map with draggable chargepoint list
 */
export default function App() {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [searchPostcode, setSearchPostcode] = useState<string | null>(null)
  const [searchCoords, setSearchCoords] = useState<{ lat: number; lng: number } | null>(null)
  // The active search term (place name or postcode); carried in the shared URL
  const [searchTerm, setSearchTerm] = useState<string | null>(() => readUrlState().q ?? null)
  const [shareCopied, setShareCopied] = useState(false)
  const { settings, updateSettings } = useSettings()

  // Detect a shared-link open (the /s endpoint redirects with ?shared=1).
  // Captured at render, before the app rewrites its own URL and drops it.
  const [openedFromShare] = useState(
    () => new URLSearchParams(window.location.search).get('shared') === '1'
  )

  // Opening a shared link shows the sender's location — reset the speed
  // filter to "All" so the recipient's own filter can't hide chargers there.
  useEffect(() => {
    if (openedFromShare) {
      updateSettings({ speedTier: 'all' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Background refresh
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger((t) => t + 1)
    }, BACKGROUND_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  const handleSelectLocation = useCallback((lat: number, lng: number, name?: string) => {
    setSearchCoords({ lat, lng })
    setSearchPostcode(null)
    setSearchTerm(name ?? null)
  }, [])

  const handleSearchPostcode = useCallback((postcode: string) => {
    setSearchPostcode(postcode)
    setSearchCoords(null)
    setSearchTerm(postcode)
  }, [])

  // Share the current view (URL carries lat/lng/q): native share sheet
  // where available, otherwise copy the link.
  // Shares point at /s (not /) so the share-og Lambda can return a
  // location-specific Open Graph preview ("Charging points near Plymouth");
  // that page redirects real browsers back to the SPA at the same view.
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/s${window.location.search}`
    try {
      if (navigator.share) {
        await navigator.share({ title: 'PowerMap.co.uk', url })
      } else {
        await navigator.clipboard.writeText(url)
        setShareCopied(true)
        setTimeout(() => setShareCopied(false), 2000)
      }
    } catch {
      // User dismissed the share sheet, or clipboard unavailable
    }
  }, [])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Offline indicator */}
      <OfflineBanner />

      {/* Top header with logo and settings */}
      <Header onSettingsClick={() => setSettingsOpen(true)} onStatsClick={() => setStatsOpen(true)} />

      {/* Fixed controls under header */}
      <div className="fixed top-14 left-0 right-0 z-40">
        {/* Location search row */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-2 max-w-lg mx-auto flex items-center gap-2">
            <LocationSearch
              onSelectLocation={handleSelectLocation}
              onSearchPostcode={handleSearchPostcode}
              initialValue={searchTerm ?? ''}
            />
            <button
              onClick={handleShare}
              className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center"
              aria-label={shareCopied ? 'Link copied' : 'Share this view'}
              title={shareCopied ? 'Link copied' : 'Share this view'}
            >
              {shareCopied ? <Check size={18} className="text-green-600" /> : <Share2 size={18} />}
            </button>
          </div>
        </div>

        {/* Speed tier toggle row */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-2 max-w-lg mx-auto">
            <SpeedTierToggle
              value={settings.speedTier}
              onChange={(speedTier) => updateSettings({ speedTier })}
            />
          </div>
        </div>
      </div>

      {/* Page content with padding for fixed header + controls (56 + 52 + 52 = 160px) */}
      <div className="pt-[160px]">
        <HybridPage
          speedTier={settings.speedTier}
          sortBy={settings.sortBy}
          onSortChange={(sortBy) => updateSettings({ sortBy })}
          refreshTrigger={refreshTrigger}
          searchPostcode={searchPostcode}
          searchCoords={searchCoords}
          searchTerm={searchTerm}
        />
      </div>

      {/* UK charging stats modal */}
      <StatsModal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        onNavigateTo={handleSelectLocation}
      />

      {/* Settings modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        updateSettings={updateSettings}
      />
    </div>
  )
}
